import { RuntimeWidget } from 'client/types';
import withErrorBoundary from 'client/widget-components/components/ErrorBoundary/withErrorBoundary';
import { useEditor } from 'client/widget-components/customHooks/useEditor';
import React, { useMemo } from 'react';
import ContainerQueryStyles from './components/ContainerQueryStyles';
import { CurrentStepComponent } from './components/steps';
import { BookerContextProvider } from './Context';
import { useResolveUrlProps } from './hooks/useResolveUrlProps';
import { useRuntimeCalBookingState } from './hooks/useRuntimeCalBookingState';
import { logBookingInfo } from './loggingUtils';
import { CalBookingWrapper } from './styles';
import { BookerContextType, CalBookingProps, DesignStyle } from './types';
import { isBookingDryRun as isBookingDryRunUtil } from './utils';

const RuntimeCalBooking: RuntimeWidget<CalBookingProps> = (
    props: CalBookingProps
) => {
    const {
        clientId,
        featureMetadata,
        widgetId,
        organizationId,
        eventIdentifier,
        events,
        designStyle: designStyleProp,
        groupedAppointmentInfo,
        serviceProviderStage,
    } = props;

    const [state, actions] = useRuntimeCalBookingState(props);
    const editor = useEditor();
    const isBookingDryRun = isBookingDryRunUtil(editor, props.featureMetadata);

    useResolveUrlProps({
        ...props,
        isBookingDryRun,
        setRescheduleUid: actions.setRescheduleUid,
        setCancelBookingId: actions.setCancelBookingId,
        setEventSlug: actions.setEventSlug,
        setTeamId: actions.setTeamId,
        setUserName: actions.setUserName,
    });

    // backward compatibility with single event booking
    const isBookingActive =
        (props.events?.length ?? 0) >= 1 || !!state.eventSlug;
    const designStyle = designStyleProp || DesignStyle.SITE_THEME;
    const currentEvent = useMemo(
        () => events?.find?.((event) => event.slug === state.eventSlug) || null,
        [events, state.eventSlug]
    );

    const bookerContext: BookerContextType = {
        ...featureMetadata,
        eventSlug: state.eventSlug,
        teamId: state.teamId,
        userName: state.userName,
        rescheduleUid: state.rescheduleUid,
        cancelBookingId: state.cancelBookingId,
        widgetId,
        designStyle,
        language: state.language,
        apiUrl: state.apiUrl,
        clientId,
        organizationId,
        currentEvent,
        eventIdentifier,
        isBookingActive,
        isBookingDryRun,
        events: events || [],
        groupedAppointmentInfo,
        serviceProviderStage,
    };

    if (!isBookingActive && !editor.isInPreviewOrEditor()) {
        logBookingInfo(
            'Trying to render RuntimeCalBooking when booking is not active in non-editor mode',
            {
                additionalInfoToLog: bookerContext,
                isInPreviewOrEditor: editor.isInPreviewOrEditor(),
            }
        );
        return null;
    }

    return (
        <CalBookingWrapper
            data-auto='ssr-cal-booking-widget'
            className={`booking-wrapper ${designStyle}`}
        >
            <BookerContextProvider
                {...bookerContext}
                state={state}
                actions={actions}
            >
                <ContainerQueryStyles widgetId={widgetId} />
                <CurrentStepComponent {...props} />
            </BookerContextProvider>
        </CalBookingWrapper>
    );
};

export default withErrorBoundary({
    Comp: RuntimeCalBooking,
    logProps: true,
    componentName: 'CalBooking',
    additionalInfoToLog: {
        tag: 'booking',
    },
});
